
import { defineComponent, ref, computed, onUnmounted } from 'vue';
import {
    IonItem,
    IonIcon,
    IonSpinner,
    loadingController,
} from '@ionic/vue';
import { useStore } from 'vuex';
import {
    closeOutline,
    musicalNote,
    playOutline,
    pauseOutline,
    cloudUploadOutline,
} from 'ionicons/icons';
import { GET, FILEUPLOAD } from '@/services/HTTP';
import { presentNativeAlert } from '@/services/Notify';
import { formatSizeKb, hasKnownMediaSizeKb, isLibraryAudioReady } from '@/utils/mediaSizeDisplay';
import { waitUntilAudioReadyToPlay } from '@/utils/audioBlobPlayback';

interface AudioFile {
    id: string;
    name: string;
    createdDate: string;
    sizeKb: number | null;
    processingMsg: string | null;
    instanceId: string | null;
    audioGroupId: string;
}

export default defineComponent({
    name: 'AudioLibraryPicker',
    components: { IonItem, IonIcon, IonSpinner },
    props: {
        audioId: {
            type: String,
            default: null,
        },
        audioName: {
            type: String,
            default: '',
        },
        audioSizeKb: {
            type: Number,
            default: null,
        },
        label: {
            type: String,
            default: 'Choose Audio',
        },
        audioGroupId: {
            type: String,
            required: true,
        },
    },
    emits: ['update:audioId', 'update:audioName', 'update:audioSizeKb'],
    setup(props, ctx) {
        const store = useStore();
        const instanceId = computed(() => store.getters['app/selectedAppInstanceId']);

        const libraryOpen = ref(false);
        const loading = ref(false);
        const audioFiles = ref<AudioFile[]>([]);
        const playingId = ref<string | null>(null);
        const currentAudio = ref<HTMLAudioElement | null>(null);
        const searchQuery = ref('');
        const sortBy = ref('date-desc');
        const pollingIds = ref(new Set<string>());

        const pollUntilReady = (id: string) => {
            if (pollingIds.value.has(id)) return;
            pollingIds.value.add(id);

            const interval = setInterval(async () => {
                // Stop polling if modal closed or file no longer in list
                const idx = audioFiles.value.findIndex(f => f.id === id);
                if (!libraryOpen.value || idx === -1) {
                    clearInterval(interval);
                    pollingIds.value.delete(id);
                    return;
                }
                try {
                    const updated: AudioFile = await GET(`/audio/${id}`, false);
                    if (updated && (hasKnownMediaSizeKb(updated.sizeKb) || updated.processingMsg)) {
                        audioFiles.value[idx] = { ...audioFiles.value[idx], ...updated };
                        clearInterval(interval);
                        pollingIds.value.delete(id);
                    }
                } catch {
                    clearInterval(interval);
                    pollingIds.value.delete(id);
                }
            }, 3000);
        };

        const filteredAudioFiles = computed(() => {
            let list = audioFiles.value;
            const q = searchQuery.value.trim().toLowerCase();
            if (q) {
                list = list.filter(f => f.name.toLowerCase().includes(q));
            }
            return [...list].sort((a, b) => {
                switch (sortBy.value) {
                    case 'date-asc':  return new Date(a.createdDate).getTime() - new Date(b.createdDate).getTime();
                    case 'name-asc':  return a.name.localeCompare(b.name);
                    case 'name-desc': return b.name.localeCompare(a.name);
                    case 'size-desc': return (b.sizeKb ?? -1) - (a.sizeKb ?? -1);
                    case 'size-asc':  return (a.sizeKb ?? Infinity) - (b.sizeKb ?? Infinity);
                    default:          return new Date(b.createdDate).getTime() - new Date(a.createdDate).getTime();
                }
            });
        });

        const selectedAudio = computed<AudioFile | null>(() => {
            // Show selected state for library reference (audioId) or legacy blob (audioName only)
            if (!props.audioId && !props.audioName) return null;
            return {
                id: props.audioId || 'legacy',
                name: props.audioName || '',
                sizeKb: props.audioSizeKb,
                processingMsg: null,
                createdDate: '',
                instanceId: instanceId.value,
                audioGroupId: props.audioGroupId,
            };
        });

        const openLibrary = async () => {
            libraryOpen.value = true;
            loading.value = true;
            try {
                audioFiles.value = await GET(`/audio/instance/${instanceId.value}`, false) ?? [];
                // Resume polling for any files still processing
                audioFiles.value.filter(f => !hasKnownMediaSizeKb(f.sizeKb) && !f.processingMsg).forEach(f => pollUntilReady(f.id));
            } finally {
                loading.value = false;
            }
        };

        const stopAudio = () => {
            if (currentAudio.value) {
                currentAudio.value.pause();
                if (currentAudio.value.src?.startsWith('blob:')) {
                    URL.revokeObjectURL(currentAudio.value.src);
                }
                currentAudio.value = null;
            }
            playingId.value = null;
        };

        const closeLibrary = () => {
            libraryOpen.value = false;
            stopAudio();
        };

        const clearSelection = () => {
            ctx.emit('update:audioId', null);
            ctx.emit('update:audioName', '');
            ctx.emit('update:audioSizeKb', null);
        };

        const selectFile = (file: AudioFile) => {
            ctx.emit('update:audioId', file.id);
            ctx.emit('update:audioName', file.name);
            ctx.emit('update:audioSizeKb', file.sizeKb);
            closeLibrary();
        };

        const handleUpload = async (event: Event) => {
            const input = event.target as HTMLInputElement;
            if (!input.files?.length) return;
            const file = input.files[0];

            const loader = await loadingController.create({ message: 'Uploading…' });
            await loader.present();
            try {
                const formData = new FormData();
                formData.append('file', file);
                formData.append('audioGroupId', props.audioGroupId);
                if (instanceId.value) formData.append('instanceId', instanceId.value);

                const created: AudioFile = await FILEUPLOAD('/audio/upload', formData);
                if (created) {
                    audioFiles.value.unshift(created);
                    // Start polling immediately — processing begins right after upload
                    pollUntilReady(created.id);
                }
            } finally {
                await loader.dismiss();
                input.value = '';
            }
        };

        const togglePlay = async (file: AudioFile) => {
            if (playingId.value === file.id) {
                stopAudio();
                return;
            }
            stopAudio();

            playingId.value = file.id;
            try {
                const result = await GET(`/audio/${file.id}/stream`, true);
                if (playingId.value !== file.id) {
                    return;
                }
                if (!result.blob.size) {
                    playingId.value = null;
                    await presentNativeAlert({
                        cssClass: 'custom-alert',
                        header: 'Playback failed',
                        message: 'The audio file is empty or still processing.',
                        buttons: ['OK'],
                    });
                    return;
                }
                const objectUrl = URL.createObjectURL(result.blob);
                const audio = new Audio();
                audio.preload = 'auto';
                audio.src = objectUrl;
                audio.onended = () => {
                    playingId.value = null;
                    currentAudio.value = null;
                    URL.revokeObjectURL(objectUrl);
                };
                currentAudio.value = audio;
                await waitUntilAudioReadyToPlay(audio);
                await audio.play();
            } catch {
                stopAudio();
                await presentNativeAlert({
                    cssClass: 'custom-alert',
                    header: 'Playback failed',
                    message: 'Could not play this file. If it was just uploaded, wait for processing to finish.',
                    buttons: ['OK'],
                });
            }
        };

        const formatDate = (iso: string) => {
            if (!iso) return '';
            return new Date(iso).toLocaleDateString();
        };

        onUnmounted(() => stopAudio());

        return {
            libraryOpen,
            loading,
            audioFiles,
            filteredAudioFiles,
            searchQuery,
            sortBy,
            playingId,
            selectedAudio,
            openLibrary,
            closeLibrary,
            clearSelection,
            selectFile,
            handleUpload,
            togglePlay,
            formatDate,
            formatSizeKb,
            hasKnownMediaSizeKb,
            isLibraryAudioReady,
            closeOutline,
            musicalNote,
            playOutline,
            pauseOutline,
            cloudUploadOutline,
        };
    },
});
